import React, { useEffect, useState } from 'react'
import HubBadge from 'components/sidebar/hubBadge'
import Select from 'components/select'
import { IPinnedProjectObject, ISimpleProject } from 'types'
import api from 'helpers/api'
import { Contributors, Views } from 'components/icons'
import { numberWithCommas } from 'components/spreadsheet/helpers/format'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'
import { HUB_BASE } from 'routes'
import Skeleton from 'components/skeleton'

interface PinnedProps {
  userId?: string
  ownProfile: boolean
}

const Pinned: React.FC<PinnedProps> = ({ userId, ownProfile }) => {
  const { displayErrorMessage } = useApplicationStore()

  const [pinnedProjects, setPinnedProjects] = useState<IPinnedProjectObject[]>([])
  const [availableProjects, setAvailableProjects] = useState<ISimpleProject[]>([])
  const [availableProjectsLoading, setAvailableProjectsLoading] = useState<boolean>(true)
  const [pinnedProjectsLoading, setPinnedProjectsLoading] = useState<boolean>(true)

  useEffect(() => {
    if (ownProfile) {
      setAvailableProjectsLoading(true)
      api
        .getPublicProjects()
        .then((response) => {
          setAvailableProjects(response.data)
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
        .finally(() => setAvailableProjectsLoading(false))
    }
  }, [ownProfile])

  useEffect(() => {
    setPinnedProjectsLoading(true)
    if (userId) {
      api
        .getPinnedProjects(userId)
        .then((response) => {
          setPinnedProjects(response.data)
          setPinnedProjectsLoading(false)
        })
        .catch((error) => {
          displayErrorMessage(error)
          setPinnedProjectsLoading(false)
        })
    } else {
      setPinnedProjects([])
      setPinnedProjectsLoading(false)
    }
  }, [userId])

  const onPinProject = (projectId: string) => {
    if (userId) {
      api({
        method: 'PUT',
        endpoint: '/user/hubs/' + projectId + '/pin'
      })
        .then((response) => {
          if (response && response.data) {
            setPinnedProjects(response.data)
          }
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }
  }

  return (
    <div>
      <div className="text-base pb-2.5">Pinned Public {capitaliseFirstLetter(PROJECT)}s</div>

      {ownProfile && (
        <div>
          <Select
            options={availableProjects.map((project) => {
              return { value: project.publicId, label: project.name }
            })}
            loading={availableProjectsLoading}
            optionsSelected={userId && pinnedProjects ? pinnedProjects.map((project) => project.publicId) : []}
            onOptionClick={(option) => {
              if (!option) return
              onPinProject(option)
            }}
            multiselect={true}
          />
        </div>
      )}
      {userId && pinnedProjects && !pinnedProjectsLoading ? (
        pinnedProjects.length !== 0 ? (
          <div className="grid grid-cols-2 max-xl:grid-cols-1 mt-2.5 gap-2.5">
            {pinnedProjects.map((project) => {
              return (
                <a
                  key={project.publicId}
                  href={`${HUB_BASE}${project.publicId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col border border-divider-strong box-border hover:shadow-heavy hover:-translate-x-px hover:-translate-y-px rounded transition-all p-[15px]"
                >
                  <div className="flex items-center">
                    <HubBadge
                      name={project.name}
                      colour={project.primaryColour}
                      textColour={project.fontColour}
                      size={40}
                    />
                    <div className="text-body font-normal px-2.5 truncate">{project.name}</div>
                  </div>
                  <div className="flex items-center flex-wrap mt-5 gap-5">
                    <div className="flex items-center text-sm text-body">
                      <Views className="h-5 w-5" />
                      <div className="ml-[5px]">{`${numberWithCommas(project.views)} view${
                        project.views === 1 ? '' : 's'
                      }`}</div>
                    </div>
                    <div className="flex items-center text-sm text-body">
                      <Contributors className="h-5 w-5" />
                      <div className="ml-[5px]">{`${numberWithCommas(project.contributors)} contributor${
                        project.contributors === 1 ? '' : 's'
                      }`}</div>
                    </div>
                  </div>
                </a>
              )
            })}
          </div>
        ) : (
          <div className="mt-2.5 italic">No pinned {PROJECT}s</div>
        )
      ) : (
        <Skeleton className="rounded mt-[15px] w-[208px] h-[130px]" />
      )}
    </div>
  )
}

export default Pinned
